import {Subscribable, BehaviorSubject, Unsubscribable} from "rxjs";

/**
 * Reduce items into an array
 * @param {T[]} previous
 * @param {T} current
 * @returns {T[]}
 */
export function arrayReduceFn<T>(previous: T[], current: T) {
  previous.push(current);
  return previous;
}

/**
 * Seed generator to create an empty array
 * @returns {any[]}
 */
export function arrayReduceSeed() {
  return [];
}

/**
 * U: type of object that contains the Subscribable
 * T: type of data returned by the object's Subscribable
 * S: type of data returned by the reduce function
 */
export class ReduceBehaviorSubject<U, T, S> extends BehaviorSubject<S> {
  private subscriptionMap: Map<U, Unsubscribable> = new Map<U, Unsubscribable>();
  private dataMap: Map<U, T> = new Map<U, T>();

  /**
   * Subscribe to an object's subscribable
   * @param {U} key - the object containing the subscribable, or some other reference to it
   * @param {Subscribable<T>} subscribable - the item to subscribe to
   */
  add(key: U, subscribable: Subscribable<T>): void {
    if (this.subscriptionMap.has(key)) return;

    const sub = subscribable.subscribe(
      (value: T) => {
        this.dataMap.set(key, value);

        let result;
        try {
          result = this.invokeReduce();
        } catch (error) {
          this.error(error);
          return;
        }

        this.next(result);
      },
      (error: any) => {
        this.error(error);
      },
      () => {
        if (this.subscriptionMap.has(key)) {
          this.subscriptionMap.delete(key);
        }
      },
    );

    this.subscriptionMap.set(key, sub);
  }

  private invokeReduce() {
    let last = this.reduceSeed();

    this.dataMap.forEach((value, key) => {
      last = this.reduceFn(last, value, key);
    });

    return last;
  }

  /**
   * Create a ReduceBehaviorSubject: combine subscribables (at runtime!) and return something like a BehaviorSubject.
   * Needs a function to combine the results and return them, and a function to generate the reducer's seed.
   * @param {(previousValue: S, currentValue: T, currentKey: U) => S} reduceFn - run iteratively against Subscribables
   * @param {() => S} reduceSeed - run to generate a seed for reduceFn each time it is invoked, and when constructed
   */
  constructor(
    private reduceFn?: (previousValue: S, currentValue: T, currentKey: U) => S,
    private reduceSeed?: () => S,
  ) {
    super(reduceSeed());
  }
}
